import moment from 'moment-timezone'
import { useCallback } from 'react'
import { isEmpty } from 'lodash'

import { useGetRawEvents } from 'Event/scenes/events/hooks/useGetRawEvents'
import { mapRawEventsResponse } from 'Event/scenes/events/helpers/mapRawEventsResponse'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { Opportunity, OpportunityApiResponse } from 'Event/interfaces'
import { useGetEvents } from './useGetEvents'
import { getSlots } from 'Event/scenes/events/helpers/volunteerSlots'

export interface EventDiffConflict {
  event: OpportunityApiResponse
  rawEvent: Partial<OpportunityApiResponse>
  errors: string[]
}

export interface EventDiffResult {
  matched: OpportunityApiResponse[]
  conflicts: any[]
  missing: OpportunityApiResponse[]
  extra: Partial<OpportunityApiResponse>[]
  success: boolean
}

export const useCompareEvents = (): [
  (from: moment.MomentInput, to: moment.MomentInput) => Promise<EventDiffResult>,
  boolean,
] => {
  const organization = useCurrentOrg()
  const [getEvents, { loading: eventsLoading, called: eventsCalled }] = useGetEvents()
  const [getRawEvents, { loading: rawEventsLoading, called: rawEventsCalled }] = useGetRawEvents()

  const compareEvents = useCallback(
    async (from: moment.MomentInput, to: moment.MomentInput): Promise<EventDiffResult> => {
      const startDate = moment.tz(from, organization.timeZone).format('MM-DD-YYYY')
      const endDate = moment.tz(to, organization.timeZone).format('MM-DD-YYYY')

      const getKey = (event: any) =>
        `${event.opportunityId}_${moment.tz(event.startsAt, organization.timeZone).format('YYYYMMDD')}`

      const isSameTime = (lhs?: Date, rhs?: Date) =>
        (!!rhs && !!lhs) || rhs === lhs ? moment.tz(lhs, organization.timeZone).isSame(rhs, 'minute') : false

      const [rawEvents, events] = await Promise.all([
        getRawEvents({
          from: startDate,
          to: endDate,
        }).then(mapRawEventsResponse),
        getEvents(startDate, endDate),
      ])

      const matched: OpportunityApiResponse[] = []
      const extra: Partial<OpportunityApiResponse>[] = []
      const missing: OpportunityApiResponse[] = []
      const conflicts: EventDiffConflict[] = []

      const rawEventsByKey = new Map<string, Partial<OpportunityApiResponse>>()
      for (const rawEvent of rawEvents) {
        const key = getKey(rawEvent)
        rawEventsByKey.set(key, rawEvent)
      }

      const eventsByKey = new Map<string, OpportunityApiResponse>()
      // needs a type because the Promise.all() array destructuring confuses our ts implementation
      for (const event of events as OpportunityApiResponse[]) {
        const key = getKey(event)
        eventsByKey.set(key, event)

        const rawEvent = rawEventsByKey.get(key)
        if (!rawEvent) {
          missing.push(event)
        } else {
          let errors = []
          if (rawEvent.occurrenceId != event.occurrenceId) errors.push('occurrence id')
          if (rawEvent.name != event.name) errors.push('name')
          if (!isSameTime(event.startsAt, rawEvent.startsAt)) errors.push('start time')
          if (!isSameTime(event.endsAt, rawEvent.endsAt)) errors.push('end time')

          const slots = event.slots || getSlots(event as unknown as Opportunity)
          const rawSlots = getSlots(rawEvent as unknown as Opportunity)

          if (slots.available !== rawSlots.available || slots.total !== rawSlots.total) {
            errors.push('registrations')
          }

          if (errors.length) {
            conflicts.push({ event: event, rawEvent, errors })
          } else {
            matched.push(event)
          }
        }
      }

      for (const [key, rawEvent] of rawEventsByKey.entries()) {
        if (!eventsByKey.has(key)) {
          extra.push(rawEvent)
        }
      }

      return {
        matched,
        conflicts,
        missing,
        extra,
        success: isEmpty(conflicts) && isEmpty(missing) && isEmpty(extra),
      }
    },
    [organization.timeZone, getRawEvents, getEvents],
  )

  const loading = !eventsCalled || eventsLoading || !rawEventsCalled || rawEventsLoading

  return [compareEvents, loading]
}
